import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { forkJoin, map, of, switchMap } from 'rxjs';
import { AuthService } from '../../core/auth/auth.service';
import { OccurrenceAction, OccurrenceFollowUp } from '../../core/occurrence/occurrence.models';
import { OccurrenceService } from '../../core/occurrence/occurrence.service';
import { ToastService } from '../../core/ui/toast.service';
import { ConfirmDialog } from '../confirm-dialog/confirm-dialog';
import { DatePicker } from '../date-picker/date-picker';
import {
  EvidenceDraft, EvidenceDraftFields, emptyEvidenceDraft, evidenceFromFollowUp, saveEvidenceDraft,
} from '../occurrence-evidences/evidence-draft';

function isoToday(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/**
 * O seguimento da ocorrência — histórico de entradas datadas, e o formulário que as regista.
 *
 * <p>Vive em dois sítios: no cartão da ficha e no separador Seguimento do formulário. Era só da
 * ficha, e quem estava a editar uma ocorrência cujo estado exige seguimento tinha de fechar o
 * painel, procurar o cartão e voltar atrás. Um componente só evita que os dois sítios acabem a
 * oferecer coisas diferentes.
 */
@Component({
  selector: 'app-occurrence-follow-ups',
  imports: [RouterLink, FormsModule, DatePipe, DatePicker, ConfirmDialog, EvidenceDraftFields],
  templateUrl: './occurrence-follow-ups.html',
})
export class OccurrenceFollowUps {
  private readonly service = inject(OccurrenceService);
  private readonly router = inject(Router);
  private readonly toasts = inject(ToastService);
  protected readonly auth = inject(AuthService);

  readonly occurrenceId = input.required<number>();
  /**
   * O estado já permite escrever seguimento? O seguimento conta o que se fez enquanto se trata,
   * por isso só começa em Em tratamento — é a mesma regra do separador no formulário. A ficha
   * deixava registar com a ocorrência ainda em Registado, e aí não há tratamento nenhum de que
   * falar.
   */
  readonly canRegister = input(true);
  /** Equipa a quem se pode partilhar uma entrada — a mesma lista dos responsáveis pela ação. */
  readonly teamMembers = input<readonly { id: number; name: string }[]>([]);
  /**
   * Alguma coisa mudou do lado do servidor. A ficha usa-o para recarregar a ocorrência: uma
   * entrada pode transferir o responsável pela implementação.
   */
  readonly changed = output<void>();

  protected readonly entries = signal<OccurrenceFollowUp[]>([]);
  protected readonly error = signal<string | null>(null);
  protected readonly showForm = signal(false);
  protected readonly saving = signal(false);
  protected readonly pendingRemoval = signal<OccurrenceFollowUp | null>(null);
  protected readonly canWrite = computed(() => this.auth.can('occurrence:write'));
  /**
   * Pode começar uma entrada agora: precisa da permissão, de um estado que já a admita e de ações
   * no tratamento — "sem tratamento, nomeadamente ações para seguir, não pode haver seguimento,
   * nem OC filhas" (revisão de 2026-10-15).
   */
  protected readonly canStart = computed(
    () => this.canWrite() && this.canRegister() && this.actions().length > 0);
  /** O estado já admite seguimento, mas falta o tratamento ter ações. */
  protected readonly missingActions = computed(
    () => this.canWrite() && this.canRegister() && this.actions().length === 0);
  /**
   * Caminho absoluto das ocorrências: o componente não sabe de que rota está a ser desenhado.
   *
   * <p>Uma string, e não um array de segmentos: o `routerLink` recebe um array de comandos, e um
   * array lá dentro não é achatado — vira parâmetros de matriz
   * (`/ocorrencias/117;0=%2F;1=acme;2=ocorrencias/nova`) e o link deixa de ir a lado nenhum.
   * O primeiro comando pode trazer barras, e essas o router separa.
   */
  protected readonly occurrencesPath = computed(
    () => `/${this.auth.currentTenant()}/ocorrencias`);

  /** Quantas entradas existem — quem o aloja mostra o número no cabeçalho. */
  readonly count = computed(() => this.entries().length);

  protected newDate = isoToday();
  protected newDescription = '';
  protected newEmailSent = false;
  /** Partilha para dentro: a quem da equipa se dá a entrada a ler. */
  protected newSharedWithUserId: number | null = null;
  /** A quem a entrada em edição já estava partilhada quando se abriu. */
  private editingSharedWithUserId: number | null = null;
  /** Com a partilha, passar-lhe também o responsável pela implementação. */
  protected newTransferResponsibility = false;

  /**
   * Passar o responsável só faz sentido ao partilhar com alguém novo. Na entrada já partilhada
   * com a mesma pessoa a troca já foi feita (ou não) quando se partilhou — e o servidor não a
   * repete.
   */
  protected canTransfer(): boolean {
    return this.newSharedWithUserId !== null
      && this.newSharedWithUserId !== this.editingSharedWithUserId;
  }
  protected newNotes = '';
  /**
   * Ações do tratamento a que a entrada diz respeito — os "#1" da tabela.
   *
   * <p>Vazia é "nenhuma em particular". Era uma só até 2026-10-08, e uma ronda de seguimento que
   * toca em três ações obrigava a três entradas iguais.
   */
  protected readonly newActionIds = signal<readonly number[]>([]);

  protected isActionSelected(actionId: number): boolean {
    return this.newActionIds().includes(actionId);
  }

  protected toggleAction(actionId: number, checked: boolean): void {
    this.newActionIds.update((ids) => checked
      ? [...ids, actionId]
      : ids.filter((id) => id !== actionId));
  }

  /** "Todas" — liga e desliga a lista inteira de uma vez. */
  protected readonly allActionsSelected = computed(() => this.actions().length > 0
    && this.actions().every((action) => this.newActionIds().includes(action.id)));

  protected toggleAllActions(checked: boolean): void {
    this.newActionIds.set(checked ? this.actions().map((action) => action.id) : []);
  }
  /** Abrir uma ocorrência a partir desta entrada, logo ao registá-la. */
  protected newTransferToOccurrence = false;

  /**
   * Evidências a registar com esta entrada (revisão de 2026-10-13): "tive de sair para registar
   * e por outro lado não fica associada àquela ação". Escrevem-se aqui com os mesmos campos da
   * secção Evidências, e gravam-se lá — já ligadas à entrada — assim que ela ficar gravada.
   */
  protected readonly evidenceDrafts = signal<readonly EvidenceDraft[]>([]);
  /** A evidência que está a ser escrita, antes de se juntar à entrada. */
  protected readonly evidenceDraft = signal<EvidenceDraft | null>(null);

  protected openEvidenceDraft(): void {
    this.error.set(null);
    this.evidenceDraft.set(emptyEvidenceDraft());
  }

  protected cancelEvidenceDraft(): void {
    this.evidenceDraft.set(null);
  }

  protected keepEvidenceDraft(): void {
    const draft = this.evidenceDraft();
    if (!draft) return;
    // A data, a descrição e as notas vêm da entrada (revisão de 2026-10-19): sem ficheiro não
    // há nada a anexar.
    if (!draft.files.length) {
      this.error.set('Escolha o ficheiro a anexar.');
      return;
    }
    this.error.set(null);
    this.evidenceDrafts.update((drafts) => [...drafts, draft]);
    this.evidenceDraft.set(null);
  }

  /** Os ficheiros de uma evidência por gravar — é o que a distingue, já que o resto é da entrada. */
  protected fileNames(draft: EvidenceDraft): string {
    return draft.files.map((file) => file.name).join(', ');
  }

  protected removeEvidenceDraft(index: number): void {
    this.evidenceDrafts.update((drafts) => drafts.filter((_, i) => i !== index));
  }
  /** Entrada que está a ser corrigida; null quando se está a criar uma. */
  protected readonly editingId = signal<number | null>(null);

  /** As ações desta ocorrência, para a entrada poder dizer a qual se refere. */
  protected readonly actions = signal<OccurrenceAction[]>([]);

  constructor() {
    // O id chega por input e muda sem o componente ser destruído (abrir outra ocorrência).
    // O load fica fora do rastreio: lê o tenant da sessão, e ao sair a sessão esvaziava-se e o
    // efeito repetia os pedidos para /t/null.
    effect(() => {
      this.occurrenceId();
      untracked(() => this.load());
    });
  }

  /** Volta a ler — as ações do tratamento podem ter mudado no cartão ao lado. */
  reload(): void {
    this.load();
  }

  private load(): void {
    this.service.followUps(this.occurrenceId()).subscribe({
      next: (list) => this.entries.set(list),
      error: () => this.entries.set([]),
    });
    this.service.actions(this.occurrenceId()).subscribe({
      next: (list) => this.actions.set(list),
      error: () => this.actions.set([]),
    });
  }

  protected openForm(): void {
    this.newDate = isoToday();
    this.newDescription = '';
    this.newEmailSent = false;
    this.newSharedWithUserId = null;
    this.editingSharedWithUserId = null;
    this.newTransferResponsibility = false;
    this.newNotes = '';
    this.newActionIds.set([]);
    this.newTransferToOccurrence = false;
    this.evidenceDrafts.set([]);
    this.evidenceDraft.set(null);
    this.emailSentNow.set(false);
    this.editingId.set(null);
    this.error.set(null);
    this.showForm.set(true);
  }

  /** Corrigir uma entrada já registada — a coluna que o documento de 2026-10-03 pediu. */
  protected openEdit(entry: OccurrenceFollowUp): void {
    this.newDate = entry.entryDate;
    this.newDescription = entry.description;
    this.newEmailSent = entry.emailSent ?? false;
    this.newNotes = entry.notes ?? '';
    this.newActionIds.set([...entry.actionIds]);
    // A partilha corrige-se aqui: mudar de pessoa ou voltar a "ninguém". Abria sempre em
    // "ninguém", e quem tinha partilhado por engano não tinha como voltar atrás (2026-10-15).
    this.newSharedWithUserId = entry.sharedWithUserId;
    this.editingSharedWithUserId = entry.sharedWithUserId;
    this.newTransferResponsibility = false;
    this.newTransferToOccurrence = false;
    this.evidenceDrafts.set([]);
    this.evidenceDraft.set(null);
    this.emailSentNow.set(false);
    this.editingId.set(entry.id);
    this.error.set(null);
    this.showForm.set(true);
  }

  /** A entrada em edição já deu origem a uma ocorrência? Então não se transfere outra vez. */
  protected editingHasDerived(): boolean {
    const id = this.editingId();
    if (id === null) return false;
    return this.entries().some((entry) => entry.id === id && entry.createdOccurrenceId != null);
  }

  /**
   * Saiu um e-mail desta entrada e ela ainda não está gravada. O pisco fica marcado e a entrada
   * não se abandona: "se envio email, o pisco fica ativo e não deve permitir sair [sem a]
   * descrição associada e respetivo registo" (revisão de 2026-10-19). O e-mail ficava nas
   * Comunicações e o seguimento sem rasto dele.
   */
  protected readonly emailSentNow = signal(false);

  protected cancelForm(): void {
    if (this.emailSentNow()) {
      this.error.set('O e-mail já foi enviado: descreva o que foi feito e registe a entrada.');
      return;
    }
    this.showForm.set(false);
    this.emailForm.set(false);
  }

  /**
   * "Adicionar botão 'Enviar email' e quando clicado o pisco 'Foi enviado email' fica
   * automaticamente selecionado" (documento de 2026-10-08).
   *
   * <p>O e-mail sai pela mesma porta das Comunicações da ocorrência — é lá que fica registado,
   * com o código no assunto, e é por lá que a resposta volta. O pisco diz só que esta entrada de
   * seguimento teve e-mail; a mensagem em si vive na conversa.
   */
  protected readonly emailForm = signal(false);
  protected readonly sendingEmail = signal(false);
  protected emailTo = '';
  protected emailSubject = '';
  protected emailBody = '';

  protected openEmail(): void {
    this.emailTo = '';
    this.emailSubject = '';
    this.emailBody = this.newDescription.trim();
    this.error.set(null);
    this.emailForm.set(true);
  }

  protected cancelEmail(): void {
    this.emailForm.set(false);
  }

  protected sendEmail(): void {
    if (!this.emailTo.trim()) {
      this.error.set('Indique o destinatário do e-mail.');
      return;
    }
    if (!this.emailSubject.trim() || !this.emailBody.trim()) {
      this.error.set('O e-mail precisa de assunto e de mensagem.');
      return;
    }
    this.error.set(null);
    this.sendingEmail.set(true);
    this.service.sendMessage(this.occurrenceId(), {
      to: this.emailTo.trim(),
      subject: this.emailSubject.trim(),
      body: this.emailBody.trim(),
      audience: 'EXTERNA',
    }).subscribe({
      next: () => {
        this.sendingEmail.set(false);
        this.emailForm.set(false);
        // O pisco fica selecionado sozinho, e preso: foi mesmo enviado.
        this.newEmailSent = true;
        this.emailSentNow.set(true);
        this.newDate = this.newDate || isoToday();
        this.toasts.success('E-mail enviado. Fica registado nas Comunicações.');
        this.changed.emit();
      },
      error: () => {
        this.sendingEmail.set(false);
        this.error.set('Não foi possível enviar o e-mail.');
      },
    });
  }

  protected add(): void {
    if (!this.newDescription.trim()) {
      this.error.set('Descreva o que foi feito.');
      return;
    }
    if (this.evidenceDraft()) {
      // Uma evidência meio escrita não se perde em silêncio: ou se anexa, ou se cancela.
      this.error.set('Anexe a evidência à entrada ou cancele-a antes de guardar.');
      return;
    }
    this.error.set(null);
    this.saving.set(true);
    const transfer = this.canTransfer() && this.newTransferResponsibility;
    const payload = {
      entryDate: this.newDate || null,
      description: this.newDescription.trim(),
      emailSent: this.newEmailSent,
      emailSentAt: this.newEmailSent ? this.newDate || null : null,
      actionIds: this.newActionIds(),
      sharedWithUserId: this.newSharedWithUserId,
      transferResponsibility: transfer,
      notes: this.newNotes.trim() || null,
    };
    const editing = this.editingId();
    const request = editing === null
      ? this.service.addFollowUp(this.occurrenceId(), payload)
      : this.service.updateFollowUp(this.occurrenceId(), editing, payload);
    const entryFields = { entryDate: this.newDate, description: this.newDescription, notes: this.newNotes };
    const drafts = this.evidenceDrafts().map((draft) => evidenceFromFollowUp(draft, entryFields));
    request.pipe(switchMap((saved) => drafts.length
      // As evidências só existem ligadas a uma entrada que já exista: gravam-se depois dela.
      ? forkJoin(drafts.map((draft) => saveEvidenceDraft(this.service, this.occurrenceId(), draft, saved.id)))
        .pipe(map((results) => ({ saved, failedFiles: results.flatMap((result) => result.failedFiles) })))
      : of({ saved, failedFiles: [] as string[] }),
    )).subscribe({
      next: ({ saved, failedFiles }) => {
        this.saving.set(false);
        this.showForm.set(false);
        this.emailSentNow.set(false);
        this.load();
        this.changed.emit();
        const evidences = drafts.length === 1 ? ' e a evidência ficou registada'
          : drafts.length > 1 ? ` e as ${drafts.length} evidências ficaram registadas` : '';
        this.toasts.success((editing === null ? 'Seguimento registado' : 'Entrada atualizada') + evidences + '.');
        if (failedFiles.length) {
          this.toasts.error(`Não foi possível enviar: ${failedFiles.join(', ')}.`);
        }
        // "Criar ocorrência associada" (era "Transferir para nova ocorrência") não obriga a passar
        // pela tabela: pede-se ao registar e abre-se a derivada já ligada a esta entrada.
        if (this.newTransferToOccurrence) {
          this.router.navigate([this.occurrencesPath(), 'nova'],
            { queryParams: { parentId: this.occurrenceId(), followUpId: saved.id } });
        }
      },
      error: () => {
        this.saving.set(false);
        this.error.set('Não foi possível guardar a entrada de seguimento.');
      },
    });
  }

  /**
   * Em que ponto vai a eliminação de uma entrada que deu origem a outra ocorrência: primeiro
   * pergunta-se se é mesmo para eliminar, depois o que fazer à ligação. Só depois de tudo
   * respondido é que a entrada desaparece — "deve permitir voltar atrás e não eliminar a linha
   * de seguimento" (documento de 2026-10-08).
   */
  protected readonly removalStep = signal<'confirmar' | 'ligacao'>('confirmar');

  protected askRemove(entry: OccurrenceFollowUp): void {
    this.removalStep.set('confirmar');
    this.pendingRemoval.set(entry);
  }

  protected cancelRemove(): void {
    this.pendingRemoval.set(null);
    this.removalStep.set('confirmar');
  }

  /** O "sim" do primeiro passo: com ocorrência derivada, falta decidir a ligação. */
  protected confirmRemove(): void {
    const entry = this.pendingRemoval();
    if (!entry) return;
    if (entry.createdOccurrenceId !== null && this.removalStep() === 'confirmar') {
      this.removalStep.set('ligacao');
      return;
    }
    this.remove(entry, true);
  }

  /** Voltar atrás na segunda pergunta sem perder a primeira. */
  protected backToConfirm(): void {
    this.removalStep.set('confirmar');
  }

  protected removeKeepingLink(): void {
    const entry = this.pendingRemoval();
    if (entry) this.remove(entry, true);
  }

  protected removeDroppingLink(): void {
    const entry = this.pendingRemoval();
    if (entry) this.remove(entry, false);
  }

  private remove(entry: OccurrenceFollowUp, keepLink: boolean): void {
    this.pendingRemoval.set(null);
    this.removalStep.set('confirmar');
    this.service.deleteFollowUp(this.occurrenceId(), entry.id, keepLink).subscribe({
      next: () => {
        this.load();
        this.changed.emit();
        this.toasts.success(entry.createdOccurrenceId !== null && !keepLink
          ? `Entrada removida. ${entry.createdOccurrenceCode ?? 'A ocorrência derivada'} deixou de estar ligada a esta.`
          : 'Entrada removida.');
      },
      error: () => this.error.set('Não foi possível remover a entrada.'),
    });
  }
}
